/**
 * The `@` picker: type `@saf` in the composer and pick the course, program or
 * person Teri should look at.
 *
 * Focus never leaves the textarea — this is a combobox popup, not a menu — so
 * the arrow/Enter/Escape keys are intercepted on `document` in the CAPTURE
 * phase. React's own listeners live on the root container, which is *inside*
 * document, so capturing here is what lets Enter pick an option instead of
 * sending the half-typed message behind it.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  AcademicCapIcon,
  RectangleStackIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { DURATION_FAST, EASE_OUT } from '@/lib/motion';
import { usePermissions } from '@/hooks/usePermissions';
import { PERM } from '@/auth/permissions';
import {
  searchHitLabel,
  searchHitSublabel,
  useEntitySearch,
} from '@/services/search';
import type { SearchHit, SearchResultType } from '@/types/search';

/** What Teri can be pointed at. Anything else is not mentionable. */
export const MENTIONABLE_TYPES: SearchResultType[] = ['course', 'program', 'user'];

const TYPE_ICON: Record<string, React.FC<{ className?: string }>> = {
  course: AcademicCapIcon,
  program: RectangleStackIcon,
  user: UserCircleIcon,
};

const TYPE_LABEL: Record<string, string> = {
  course: 'Course',
  program: 'Program',
  user: 'Person',
};

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

interface MentionPopoverProps {
  /** The textarea the popover hangs off (and whose caret it follows). */
  anchorRef: React.RefObject<HTMLElement>;
  /** The `@token` the member has typed, without the `@`. */
  query: string;
  /** Types to offer; narrowed further by the member's own permissions. */
  types?: SearchResultType[];
  onSelect: (hit: SearchHit) => void;
  onDismiss: () => void;
  /** Lets the composer put `aria-activedescendant` on the textarea itself. */
  onActiveOptionChange?: (optionId: string | null) => void;
}

const MentionPopover: React.FC<MentionPopoverProps> = ({
  anchorRef,
  query,
  types = MENTIONABLE_TYPES,
  onSelect,
  onDismiss,
  onActiveOptionChange,
}) => {
  const { can } = usePermissions();
  const reduceMotion = useReducedMotion();
  const debouncedQuery = useDebounced(query, 250);
  const [active, setActive] = useState(0);
  const [style, setStyle] = useState<React.CSSProperties>({});

  // People are only offered to members who may look at member data. The
  // backend drops a disallowed mention anyway — this is UX, not security:
  // offering a name you cannot ask about is a promise the answer won't keep.
  const permittedTypes = useMemo(
    () => types.filter((t) => (t === 'user' ? can(PERM.ANALYTICS_VIEW) : true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [types.join(','), can]
  );

  const { data, isLoading, isError } = useEntitySearch(debouncedQuery, permittedTypes);
  // Stable identity: the key handler below re-registers whenever `hits` change.
  const hits = useMemo(() => data ?? [], [data]);

  // The composer is pinned to the bottom of the panel, so the list opens
  // upward from the textarea's top edge. Same measure-the-anchor approach as
  // SearchableSelect, portaled for the same reason: the panel clips overflow.
  const updatePosition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setStyle({
      position: 'fixed',
      bottom: Math.max(window.innerHeight - rect.top + 8, 8),
      left: rect.left,
      width: Math.max(rect.width, 240),
      maxHeight: 'min(260px, 40vh)',
      zIndex: 70,
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, hits.length, isLoading]);

  useEffect(() => {
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updatePosition]);

  // A new set of hits invalidates the old cursor.
  useEffect(() => {
    setActive(0);
  }, [debouncedQuery, hits.length]);

  const activeOptionId = hits[active] ? `teri-mention-${hits[active].id}` : null;
  useEffect(() => {
    onActiveOptionChange?.(activeOptionId);
    return () => onActiveOptionChange?.(null);
  }, [activeOptionId, onActiveOptionChange]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const swallow = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      if (e.key === 'Escape') {
        swallow();
        onDismiss();
        return;
      }
      if (e.key === 'ArrowDown') {
        swallow();
        setActive((i) => (hits.length ? (i + 1) % hits.length : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        swallow();
        setActive((i) => (hits.length ? (i - 1 + hits.length) % hits.length : 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const hit = hits[active];
        // With nothing to pick, Enter belongs to the composer — a member who
        // typed "@" and gave up must still be able to send their message.
        if (!hit) return;
        swallow();
        onSelect(hit);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [active, hits, onDismiss, onSelect]);

  const body = (
    <motion.div
      role="listbox"
      aria-label="Mention a course, program or person"
      aria-activedescendant={activeOptionId ?? undefined}
      data-testid="teri-mention-popover"
      style={{ ...style, transformOrigin: 'bottom left' }}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 4, scale: 0.98 }}
      transition={{ duration: DURATION_FAST + 0.03, ease: EASE_OUT }}
      className="overflow-y-auto scrollbar-thin rounded-xl bg-surface-elevated border border-border-primary/20 shadow-2xl p-1"
    >
      {isLoading ? (
        <div className="p-1 space-y-1" data-testid="teri-mention-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5" aria-hidden="true">
              <span className="w-5 h-5 rounded-full bg-text-tertiary/15 animate-pulse" />
              <span
                className="h-3 rounded bg-text-tertiary/15 animate-pulse"
                style={{ width: `${60 - i * 10}%` }}
              />
            </div>
          ))}
          <span className="sr-only">Searching…</span>
        </div>
      ) : isError ? (
        <p className="px-3 py-3 text-[13px] text-text-secondary">
          Couldn't search just now.
        </p>
      ) : hits.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-text-tertiary">
          Nothing matches "{debouncedQuery}"
        </p>
      ) : (
        hits.map((hit, index) => {
          const Icon = TYPE_ICON[hit.type] ?? AcademicCapIcon;
          const sublabel = searchHitSublabel(hit);
          const selected = index === active;
          return (
            <button
              key={`${hit.type}:${hit.id}`}
              id={`teri-mention-${hit.id}`}
              type="button"
              role="option"
              aria-selected={selected}
              // Mouse must not steal focus from the textarea, or the caret
              // position the token replacement depends on is lost.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => onSelect(hit)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors duration-fast ${
                selected ? 'bg-primary-500/10' : 'hover:bg-text-tertiary/10'
              }`}
            >
              <Icon className="w-[18px] h-[18px] shrink-0 text-text-tertiary" />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-text-primary truncate leading-tight">
                  {searchHitLabel(hit)}
                </span>
                {sublabel && (
                  <span className="block text-[11px] text-text-tertiary truncate leading-tight">
                    {sublabel}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                {TYPE_LABEL[hit.type] ?? hit.type}
              </span>
            </button>
          );
        })
      )}
    </motion.div>
  );

  return createPortal(<AnimatePresence>{body}</AnimatePresence>, document.body);
};

export default MentionPopover;
